import { Alert, Button, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import moment from "moment";
import { useMemo } from "react";
import * as Yup from "yup";

import { ContractHelper } from "@/common/helpers/entity/contract";
import { FormikHelper } from "@/common/helpers/formik";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto, ContractVehicleCheckInDto, SpotType } from "@/core/api/generated/v1";

import GeneralValidationError from "../../Error/GeneralValidationError";
import FormActions from "../../Form/FormActions";
import ContractSpotInfoInput from "./ContractSpotInfoInput";

export interface ContractVehicleCheckInOwnProps {
  contract: ContractDto;
  onSave?: (newValue: ContractDto) => void;
}

export type ContractVehicleCheckInProps = ContractVehicleCheckInOwnProps;

export default function ContractVehicleCheckIn({ contract, onSave }: ContractVehicleCheckInProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();

  const contractComputedState = useMemo(
    () => ContractHelper.getComputedState(contract),
    [contract],
  );

  return (
    <Stack spacing={2}>
      {!contractComputedState?.vehicleCheckIn?.can && (
        <Alert severity='warning'>
          <Box>Vehicle check out is not allowed at the moment.</Box>
          {contractComputedState?.vehicleCheckIn?.reason && (
            <Box>{contractComputedState?.vehicleCheckIn?.reason}</Box>
          )}
        </Alert>
      )}

      {contractComputedState?.vehicleCheckIn?.can && (
        <Formik<ContractVehicleCheckInDto & BaseFormikValues>
          enableReinitialize
          initialValues={{
            spotInfo:
              (contract?.checkInSpotInfo
                ? { ...contract?.checkInSpotInfo, spotId: contract?.checkInSpotInfo?.spot?.id }
                : undefined) || undefined,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // vehicleId: Yup.string().required("Vehicle is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            const values2: typeof values = {
              ...values,
              spotInfo: values.spotInfo
                ? {
                    ...values.spotInfo,
                    date: values.spotInfo?.date && moment(values.spotInfo?.date).utc().format(),
                  }
                : undefined,
            };

            try {
              const response =
                await apiClient.contractsApi.apiV1ContractsContractIdVehicleCheckInPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  contractId: contract?.id || "",
                  contractVehicleCheckInDto: {
                    ...values2,
                  },
                });
              enqueueSnackbar("Saved.", { variant: "success" });
              onSave && onSave(response.data);

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const { errors, submitForm, handleSubmit, isSubmitting } = formikProps;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <Box>
                    <ContractSpotInfoInput
                      contractId={contract.id}
                      spotInfo={contract.checkInSpotInfo}
                      formikProps={FormikHelper.getSubProps(
                        formikProps,
                        "spotInfo",
                        (v) => v.spotInfo,
                      )}
                      spotType={SpotType.CheckIn}
                    />
                  </Box>

                  <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                  <FormActions>
                    <Button
                      color='primary'
                      loading={isSubmitting}
                      fullWidth
                      type='submit'
                      variant='contained'
                      onClick={submitForm}
                    >
                      Save
                    </Button>
                  </FormActions>
                </Stack>
              </form>
            );
          }}
        </Formik>
      )}
    </Stack>
  );
}
